import { RenderStateCamera, View } from "@novorender/api";
import { mat3, quat, vec3 } from "gl-matrix";
import { MutableRefObject, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { store } from "app/store";
import { cameraStateActions, useDispatchCameraState } from "contexts/cameraState";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectAlignmentView } from "features/alignment";
import { AlignmentView } from "features/alignment/types";
import { measureActions } from "features/measure";
import { orthoCamActions, selectCurrentTopDownElevation } from "features/orthoCam";
import { undoRedoActions } from "features/undoRedo";
import { explorerActions, selectIsProcoreDesign, selectNewDesign } from "slices/explorer";
import { ViewMode } from "types/misc";
import { log } from "utils/log";

import {
    renderActions,
    selectCameraType,
    selectClippingInEdit,
    selectClippingPlanes,
    selectViewMode,
} from "../renderSlice";
import { CameraType, DeepMutable } from "../types";
import {
    consumeSuppressedCameraHistoryRecording,
    consumeSuppressedOrthoClippingSync,
} from "./cameraHistorySuppression";

const l = log.camera.useHandleCameraMoved;

export function cloneCameraState(camera: RenderStateCamera): RenderStateCamera {
    return {
        ...camera,
        position: vec3.clone(camera.position),
        rotation: quat.clone(camera.rotation),
        pivot: camera.pivot ? vec3.clone(camera.pivot) : undefined,
    };
}

export function useHandleCameraMoved({
    engine2dRenderFnRef,
    containers,
}: {
    engine2dRenderFnRef: MutableRefObject<((moved: boolean, idleframe: boolean) => void) | undefined>;
    containers: ({ update: () => void } | null)[];
}) {
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const dispatchCameraState = useDispatchCameraState();
    const cameraType = useAppSelector(selectCameraType);
    const viewMode = useAppSelector(selectViewMode);
    const editClipping = useAppSelector(selectClippingInEdit);
    const currentTopDownElevation = useAppSelector(selectCurrentTopDownElevation);
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const alignmentView = useAppSelector(selectAlignmentView);
    const clippingPlanes = useAppSelector(selectClippingPlanes);

    const movementTimer = useRef<ReturnType<typeof setTimeout>>();
    const orthoMovementTimer = useRef<ReturnType<typeof setTimeout>>();
    const movementTimerProcoreDesign = useRef<ReturnType<typeof setTimeout>>();

    const prevCameraType = useRef(cameraType);
    const prevCameraFar = useRef(0);
    const prevCameraPosition = useRef(vec3.create());
    const prevCameraRotation = useRef(quat.create());
    const prevClippingPlaneW = useRef<number>(0);

    const first = useRef(true);
    const lastSavedCameraState = useRef<RenderStateCamera | null>(null);
    const prevLastSavedCameraState = useRef<RenderStateCamera | null>(null);
    const undoTriggeredMove = useRef(false);
    const firstMoveDone = useRef(false);
    const cameraMovedId = useRef(0);

    const clippingPlanesRef = useRef(clippingPlanes);
    clippingPlanesRef.current = clippingPlanes;

    useEffect(
        function initCameraMovedTracker() {
            if (!view) {
                return;
            }

            view.render = ({ isIdleFrame, cameraMoved }) => cameraMovedTrigger(isIdleFrame, cameraMoved, view);

            function cameraMovedTrigger(isIdleFrame: boolean, cameraMoved: boolean, view: View) {
                const moved =
                    cameraMoved ||
                    prevCameraType.current !== cameraType ||
                    prevCameraFar.current !== view.renderState.camera.far ||
                    !vec3.exactEquals(prevCameraPosition.current, view.renderState.camera.position) ||
                    !quat.exactEquals(prevCameraRotation.current, view.renderState.camera.rotation);
                prevCameraType.current = cameraType;
                prevCameraFar.current = view.renderState.camera.far;
                prevCameraPosition.current = vec3.clone(view.renderState.camera.position);
                prevCameraRotation.current = quat.clone(view.renderState.camera.rotation);

                engine2dRenderFnRef.current?.(moved, isIdleFrame);

                const lastPos = lastSavedCameraState.current;
                if (lastPos === null) {
                    // The first move should not be recorded as an undoable
                    // action, but we do need to set the last saved camera state
                    lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
                    l.debug("Initial camera state set:", lastSavedCameraState.current);
                    return;
                }

                const currPos = view.renderState.camera;
                const posChanged = !vec3.exactEquals(currPos.position, lastPos.position);
                const rotChanged = !quat.exactEquals(currPos.rotation, lastPos.rotation);
                const fovChanged = lastPos.fov !== currPos.fov;
                const kindChanged = lastPos.kind !== currPos.kind;
                const movedReally = moved && (posChanged || rotChanged || fovChanged || kindChanged);

                if (!movedReally) {
                    return;
                }

                internalEventEmitter.emit("cameraUpdated", view.renderState.camera);

                containers.forEach((container) => container && container.update());
                dispatch(renderActions.setStamp(null));
                dispatch(measureActions.selectHoverObj(undefined));
                dispatchCameraState(cameraStateActions.set(view.renderState.camera));

                dispatch(explorerActions.setAppliedViewpointToViewerInteraction());

                if (movementTimer.current) {
                    clearTimeout(movementTimer.current);
                }

                if (movementTimerProcoreDesign.current) {
                    clearTimeout(movementTimerProcoreDesign.current);
                }

                if (orthoMovementTimer.current) {
                    clearTimeout(orthoMovementTimer.current);
                }

                orthoMovementTimer.current = setTimeout(() => {
                    if (
                        cameraType !== CameraType.Orthographic ||
                        view.renderState.camera.kind !== "orthographic" ||
                        alignmentView === AlignmentView.OrthoTopDown ||
                        editClipping ||
                        undoTriggeredMove.current
                    ) {
                        return;
                    }

                    const { camera } = view.renderState;

                    // Update elevation
                    const mat = mat3.fromQuat(mat3.create(), camera.rotation);
                    const up = [0, 0, 1] as vec3;
                    const topDown = vec3.equals(vec3.fromValues(mat[6], mat[7], mat[8]), up);
                    const elevation = topDown ? view.renderState.camera.position[2] : undefined;
                    if (currentTopDownElevation !== elevation) {
                        dispatch(orthoCamActions.setCurrentTopDownElevation(elevation));
                    }

                    // Move grid
                    const origin = vec3.clone(view.renderState.grid.origin);
                    const z = vec3.fromValues(mat[6], mat[7], mat[8]);
                    const camPos = vec3.clone(camera.position);
                    const delta = vec3.dot(z, vec3.sub(vec3.create(), camPos, origin));
                    const newPos = vec3.scaleAndAdd(vec3.create(), origin, z, delta);
                    dispatch(renderActions.setGrid({ origin: newPos }));

                    // Move clipping plane
                    if (!newDesign && !isProcoreDesign) {
                        const planeIndex = view.renderState.clipping.planes.findIndex(
                            ({ normalOffset: [nx, ny, nz] }) =>
                                // Parallel to camera direction
                                Math.abs(vec3.dot(z, vec3.fromValues(nx, ny, nz))) > 0.999,
                        );

                        if (planeIndex === -1) {
                            return;
                        }

                        const plane = view.renderState.clipping.planes[planeIndex];
                        if (Math.abs(plane.normalOffset[3] - prevClippingPlaneW.current) <= 0.001) {
                            return;
                        }

                        const w = vec3.dot(z, camera.position);
                        prevClippingPlaneW.current = w;

                        if (first.current) {
                            //Do not move clipping plane on startup
                            first.current = false;
                            return;
                        }

                        if (consumeSuppressedOrthoClippingSync()) {
                            // Do not move clipping plane if it was explicitly suppressed.
                            return;
                        }

                        dispatch(
                            renderActions.setClippingPlanes({
                                planes: view.renderState.clipping.planes.map((p, i) => {
                                    if (i === planeIndex) {
                                        const deltaW = w - p.normalOffset[3];
                                        const normal = vec3.fromValues(
                                            p.normalOffset[0],
                                            p.normalOffset[1],
                                            p.normalOffset[2],
                                        );
                                        const currentAnchorPos = clippingPlanesRef.current.planes[i]?.anchorPos;
                                        const anchorPos = currentAnchorPos
                                            ? vec3.scaleAndAdd(vec3.create(), currentAnchorPos, normal, deltaW)
                                            : undefined;
                                        return {
                                            ...p,
                                            normalOffset: [
                                                p.normalOffset[0],
                                                p.normalOffset[1],
                                                p.normalOffset[2],
                                                w,
                                            ] as Vec4,
                                            baseW: w,
                                            ...(anchorPos ? { anchorPos } : {}),
                                        };
                                    }
                                    return { ...p, baseW: p.normalOffset[3] };
                                }) as DeepMutable<ReturnType<typeof selectClippingPlanes>["planes"]>,
                            }),
                        );
                    }
                }, 100);

                movementTimer.current = setTimeout(() => {
                    if (viewMode === ViewMode.Panorama) {
                        return;
                    }

                    const { position, rotation, fov } = view.renderState.camera;

                    dispatch(
                        renderActions.saveCameraPosition({
                            kind: cameraType,
                            position: vec3.clone(position),
                            rotation: quat.clone(rotation),
                            fov,
                        }),
                    );
                }, 500);

                // This uses the undo/redo system instead of saveCameraPosition,
                // which is exposed in the Procore design. This can become the
                // only timer if we migrate standalone back button to use the
                // undo/redo system as well.
                movementTimerProcoreDesign.current = setTimeout(() => {
                    if (viewMode === ViewMode.Panorama) {
                        return;
                    }

                    const currentCameraMovedId = cameraMovedId.current;
                    prevLastSavedCameraState.current = lastSavedCameraState.current;
                    lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
                    l.debug(
                        `prevLastSavedCameraState: ${currentCameraMovedId}:`,
                        prevLastSavedCameraState.current ? prevLastSavedCameraState.current.position : null,
                    );
                    l.debug(
                        `lastSavedCameraState ${currentCameraMovedId}:`,
                        lastSavedCameraState.current ? lastSavedCameraState.current.position : null,
                    );

                    if (firstMoveDone.current === false) {
                        // The first move should not be recorded as an undoable
                        // action, so we set the flag and return
                        firstMoveDone.current = true;
                        l.debug("First camera move done, not recording");
                        return;
                    }

                    if (undoTriggeredMove.current) {
                        // Do not record camera move if it was triggered by an undo/redo action
                        l.debug("Camera move triggered by undo/redo, not recording");
                        undoTriggeredMove.current = false;
                        prevLastSavedCameraState.current = lastSavedCameraState.current;
                        return;
                    }

                    if (editClipping) {
                        // Do not record camera move during clipping edit.
                        l.debug("Camera move during clipping edit, not recording");
                        return;
                    }

                    if (consumeSuppressedCameraHistoryRecording()) {
                        // Do not record camera move if it was explicitly suppressed.
                        l.debug("Camera move explicitly suppressed, not recording");
                        prevLastSavedCameraState.current = lastSavedCameraState.current;
                        return;
                    }

                    const prevLastSavedCameraStateClone = prevLastSavedCameraState.current
                        ? cloneCameraState(prevLastSavedCameraState.current)
                        : null;
                    const lastSavedCameraStateClone = lastSavedCameraState.current
                        ? cloneCameraState(lastSavedCameraState.current)
                        : null;

                    if (store.getState().render.recordCameraMoveUndoRedo) {
                        dispatch(
                            undoRedoActions.push({
                                description: `Camera moved ${currentCameraMovedId}`,
                                undoCallback: () => {
                                    if (!prevLastSavedCameraStateClone) {
                                        return;
                                    }
                                    l.debug(
                                        `Undoing camera move ${currentCameraMovedId}`,
                                        prevLastSavedCameraStateClone,
                                    );
                                    undoTriggeredMove.current = true;

                                    dispatch(
                                        renderActions.setCamera({
                                            type:
                                                prevLastSavedCameraStateClone.kind === "pinhole"
                                                    ? CameraType.Pinhole
                                                    : CameraType.Orthographic,
                                            goTo: prevLastSavedCameraStateClone,
                                        }),
                                    );
                                },
                                redoCallback: () => {
                                    if (!lastSavedCameraStateClone) {
                                        return;
                                    }
                                    l.debug(`Redoing camera move ${currentCameraMovedId}`, lastSavedCameraStateClone);
                                    undoTriggeredMove.current = true;

                                    dispatch(
                                        renderActions.setCamera({
                                            type:
                                                lastSavedCameraStateClone.kind === "pinhole"
                                                    ? CameraType.Pinhole
                                                    : CameraType.Orthographic,
                                            goTo: lastSavedCameraStateClone,
                                        }),
                                    );
                                },
                            }),
                        );
                    } else {
                        dispatch(renderActions.setRecordCameraMoveUndoRedo(true));
                    }
                    cameraMovedId.current += 1;
                    l.debug(`Last camera state updated ${currentCameraMovedId}`);
                    lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
                }, 500);
            }
        },
        [
            view,
            dispatch,
            dispatchCameraState,
            currentTopDownElevation,
            cameraType,
            viewMode,
            engine2dRenderFnRef,
            editClipping,
            containers,
            newDesign,
            isProcoreDesign,
            alignmentView,
            internalEventEmitter,
        ],
    );
}
